import React from "react";
import { formatDate, formatNumber } from "@/lib/utils";
import { getCompanyConfig } from "@/config/company";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface MoldPrintTemplateProps {
  data?: any;
  mold?: any;
  config?: any;
}

export default function MoldPrintTemplate({
  data,
  mold,
  config,
}: MoldPrintTemplateProps) {
  const d = data || mold || {};
  const c = getCompanyConfig(config);
  const docId = d?.code || d?.moldId || d?.id || "";

  // Maintenance records (real or padded for shop floor handwriting)
  const logs = d.maintenanceLogs || d.logs || [];
  const minRows = 8;
  const rowsCount = Math.max(logs.length, minRows);

  // 4-tier location hierarchy or warehouse location
  const locationDisplay =
    d.warehouseLocation?.code ||
    d.location ||
    (d.rackZone
      ? `${d.rackZone}${d.rackAisle ? `-${d.rackAisle}` : ""}${d.rackShelf ? `-${d.rackShelf}` : ""}${d.rackBin ? `-${d.rackBin}` : ""}`
      : "Khu vực kho khuôn");

  return (
    <div
      className="kh-print-doc bg-white w-full mx-auto p-[5mm] text-black"
      style={{
        fontFamily: "'Times New Roman', Times, serif",
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
      id="print-area"
    >
      {/* 1. Header Công ty */}
      <ISOPrintHeader
        config={config}
        className="mb-3"
      />

      {/* 2. Tiêu Đề Biểu Mẫu Căn Giữa Đối Xứng */}
      <PrintDocumentTitle
        title="THẺ THEO DÕI KHUÔN BẾ"
        subtitle="DIE-CUT MOLD CARD"
        docNo={docId}
        docNoLabel="Mã Khuôn"
        date={formatDate(d.createdAt || new Date())}
        dateLabel="Ngày Đưa Vào SD"
        className="my-3 pb-2 border-b border-black"
      />

      {/* 3. Thông Tin Kỹ Thuật Chi Tiết Của Khuôn */}
      <div className="mb-4">
        <div className="bg-slate-100 font-bold text-[12px] px-2 py-1 border border-black uppercase mb-2">
          1. THÔNG SỐ KỸ THUẬT KHUÔN BẾ
        </div>
        <PrintInfoGrid
          labelWidth="w-[140px]"
          leftColumn={[
            { label: 'Tên khuôn / SP', labelEn: 'Mold / Product', value: d.name || '—', valueClassName: 'font-bold' },
            {
              label: 'Tương thích máy bế',
              labelEn: 'Machine',
              value: Array.isArray(d.machineCompat) ? d.machineCompat.join(", ") : d.machineCompat || "Máy bế tự động / Bán tự động",
            },
            {
              label: 'Khổ ván khuôn',
              labelEn: 'Size (D x R)',
              value: d.sizeX && d.sizeY ? `${d.sizeX} x ${d.sizeY} cm` : "Theo thiết kế chuẩn",
              valueClassName: 'font-mono',
            },
            {
              label: 'Vị trí lưu kho kệ',
              labelEn: 'Location',
              value: locationDisplay,
              valueClassName: 'font-bold text-emerald-800 font-mono',
            },
          ]}
          rightColumn={[
            {
              label: 'Khách hàng',
              labelEn: 'Customer',
              value: d.customer?.name || d.customerName || "Nội bộ / APV",
            },
            {
              label: 'Số con / Khuôn (Ups)',
              labelEn: 'Ups',
              value: `${d.ups || "1"} con/khuôn`,
              valueClassName: 'font-mono font-bold text-indigo-700',
            },
            {
              label: 'Tổng mét dao/cấn',
              labelEn: 'Rule Length',
              value: d.totalMeters ? `${d.totalMeters} m` : "—",
              valueClassName: 'font-mono',
            },
            {
              label: 'Tuổi thọ / Lượt dập',
              labelEn: 'Strokes',
              value: `Đã dập: ${formatNumber(d.totalImpressions || d.totalStrokes || 0)} / ${formatNumber(d.maxImpressions || d.lifespan || 100000)} lượt`,
              valueClassName: 'font-mono text-[11px]',
            },
          ]}
        />
      </div>

      {/* 4. Nhật Ký Sử Dụng & Bảo Dưỡng (Maintenance Logs) */}
      <div className="mb-4">
        <div className="bg-slate-100 font-bold text-[12px] px-2 py-1 border border-black uppercase flex justify-between items-center">
          <span>2. NHẬT KÝ SỬ DỤNG & BẢO DƯỠNG KHUÔN (LADE LOGS)</span>
          <span className="text-[10px] lowercase font-normal italic">(ghi nhận sau mỗi ca sản xuất hoặc bảo trì)</span>
        </div>
        <table className="w-full border-collapse border border-black border-t-0 text-[11px] font-sans">
          <thead>
            <tr className="bg-slate-50 border-b border-black text-slate-800">
              <th className="border-r border-black py-1.5 px-2 text-center w-10">STT</th>
              <th className="border-r border-black py-1.5 px-2 text-center w-24">Ngày tháng</th>
              <th className="border-r border-black py-1.5 px-2 text-center w-28">Lệnh SX (LSX)</th>
              <th className="border-r border-black py-1.5 px-2 text-center w-24">Số lượng bế</th>
              <th className="border-r border-black py-1.5 px-2 text-left">Tình trạng / Nội dung bảo dưỡng (Thay dao, mài nhíp, thay mút...)</th>
              <th className="py-1.5 px-2 text-center w-20">Ký tên</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rowsCount }).map((_, idx) => {
              const log = logs[idx];
              return (
                <tr key={`log-${idx}`} className="border-b border-black last:border-b-0 h-8">
                  <td className="border-r border-black text-center font-mono">{idx + 1}</td>
                  <td className="border-r border-black text-center font-mono">
                    {log?.date ? formatDate(log.date) : ""}
                  </td>
                  <td className="border-r border-black text-center font-mono font-medium">
                    {log?.orderId || log?.productionOrderId || ""}
                  </td>
                  <td className="border-r border-black text-right pr-2 font-mono">
                    {log?.qty ? formatNumber(log.qty) : ""}
                  </td>
                  <td className="border-r border-black px-2 text-slate-800">
                    {log?.notes || log?.description || ""}
                  </td>
                  <td className="text-center font-medium">{log?.performer || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 5. Chữ Ký Xác Nhận */}
      <div className="grid grid-cols-4 gap-2 text-center mt-6 break-inside-avoid text-[12px] font-sans">
        <div>
          <div className="font-bold uppercase">LẬP THẺ KHUÔN</div>
          <div className="italic text-slate-500 text-[11px] mb-14">(Ký & ghi rõ họ tên)</div>
          <div className="font-semibold">{d.createdBy || d.creator?.fullName || "Kỹ thuật APV"}</div>
        </div>
        <div>
          <div className="font-bold uppercase">THỢ BẾ NHẬN</div>
          <div className="italic text-slate-500 text-[11px] mb-14">(Ký nhận khuôn)</div>
          <div className="font-semibold">&nbsp;</div>
        </div>
        <div>
          <div className="font-bold uppercase">QUẢN ĐỐC XƯỞNG</div>
          <div className="italic text-slate-500 text-[11px] mb-14">(Kiểm tra & xác nhận)</div>
          <div className="font-semibold">&nbsp;</div>
        </div>
        <div>
          <div className="font-bold uppercase">TRƯỞNG PHÒNG KT</div>
          <div className="italic text-slate-500 text-[11px] mb-14">(Phê duyệt & lưu thẻ)</div>
          <div className="font-semibold">&nbsp;</div>
        </div>
      </div>
    </div>
  );
}
